import { MotionValue, motion, useSpring, useTransform } from 'framer-motion'
import { useEffect } from 'react'
import { cn } from '~/src/lib/utils'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  value: number
  height: number
}

function AnimatedCounter({ value, height = 18, className, ...props }: Props) {
  // Format value with commas
  const valueStr = value.toLocaleString()
  const digits = valueStr.split('')

  // Calculate the place value for each digit (ignoring commas)
  let digitPlaces: number[] = []
  let place = Math.pow(10, valueStr.replace(/,/g, '').length - 1)
  for (let char of digits) {
    if (/\d/.test(char)) {
      digitPlaces.push(place)
      place = place / 10
    } else {
      digitPlaces.push(0) // placeholder for comma
    }
  }

  return (
    <div className={cn('inline-flex overflow-hidden', className)} {...props}>
      {digits.map((char, i) => {
        if (/\d/.test(char)) {
          const thisPlace = digitPlaces[i] ?? 0
          const digit = (
            <Digit key={i} place={thisPlace} value={value} height={height} />
          )
          return digit
        } else {
          // Render comma
          return (
            <span
              key={i}
              style={{ height }}
              className='relative flex w-[0.5ch] select-none items-center justify-center tabular-nums'
            >
              ,
            </span>
          )
        }
      })}
    </div>
  )
}

function Digit({
  place,
  value,
  height,
}: {
  place: number
  value: number
  height: number
}) {
  let valueRoundedToPlace = Math.floor(value / place)
  let animatedValue = useSpring(valueRoundedToPlace, {
    stiffness: 100,
    damping: 20,
  })

  useEffect(() => {
    animatedValue.set(valueRoundedToPlace)
  }, [animatedValue, valueRoundedToPlace])

  return (
    <div style={{ height }} className='relative w-[1ch] tabular-nums'>
      {[...Array(10).keys()].map((i) => (
        <Number key={i} mv={animatedValue} number={i} height={height} />
      ))}
    </div>
  )
}

function Number({
  mv,
  number,
  height,
}: {
  mv: MotionValue
  number: number
  height: number
}) {
  let y = useTransform(mv, (latest) => {
    let placeValue = latest % 10
    let offset = (10 + placeValue - number) % 10

    let memo = -offset * height

    if (offset > 5) {
      memo += 10 * height
    }

    return memo
  })

  return (
    <motion.span
      style={{ y }}
      className='absolute inset-0 flex items-center justify-center'
    >
      {number}
    </motion.span>
  )
}

export default AnimatedCounter
